'use strict';

import { Component } from 'react';
import PropTypes from 'prop-types';
import debounce from 'lodash.debounce';
import indexBy from 'lodash.indexby';
import Paginator from '@components/Search/Paginator.react';
import SearchKeywords from '@components/Search/Filters/SearchKeywords.react';
import FiltersModal from '@components/Search/Modals/AddSwapRecipe/Restaurants/FiltersModal.react';
import MacrosPieChart from '@components/Widgets/MacrosPieChart.react';
import LocationInfo from '@components/Products/LocationInfo.react';
import ContactFormModal from '@components/Widgets/ContactFormModal.react';
import ViolationsPopup from '@components/Widgets/ViolationsPopup.react';

import FoodDetailsModal from '@components/Search/Modals/FoodDetailsModal.react';

import AuthStore from '@stores/AuthStore';

import allNutrients from '@tables/nutrients';
import Analytics from '@utils/Analytics';
import { fetchDocumentsById } from '@utils/Content';
import { getConfig } from '@utils/Env';
import { getDemographic } from '@utils/Nutrition';
import { roundForHumans, roundForHumansByUnit } from '@utils/Math';
import {
    getIdealsFromEnvelope,
    transformEnvelopeForRestaurants,
    convertEnvelopeToFilters,
} from '@utils/Nutrition';
import allRecommendations from '@tables/recommendations';
import './Menu.scss';

const nutrKeys = {
    208: {
        Units: '',
        NutrDesc: 'Calories',
    },
    VEG: {
        Units: ' servings',
        NutrDesc: 'Vegetables',
    },
    FRU: {
        Units: ' servings',
        NutrDesc: 'Fruit',
    },
};

const nutrientWhitelist = ['208', '203', '204', '205', '307', 'ASG'];

export default class RestaurantMenu extends Component {
    static propTypes = {
        mode: PropTypes.string,

        selectedLocation: PropTypes.object.isRequired,
    };

    static defaultProps = {
        mode: 'restaurants',
    };

    static contextTypes = {
        addSwapContext: PropTypes.object,
        onSelectProducts: PropTypes.func,
        showCreateCustom: PropTypes.func,

        meals: PropTypes.array,
        recipes: PropTypes.object,
        foods: PropTypes.object,

        confirm: PropTypes.func,
        isMobile: PropTypes.bool,
    };

    static childContextTypes = {
        showFoodDetails: PropTypes.func,
    };

    constructor(props, context) {
        super(props, context);

        // Based on our addSwapContext and meals already scheduled on this date,
        // determine two sets of envelopes for this user.
        // 1. The per-meal envelope for mealType considering other meals today - Good Choice
        // 2. The per-meal envelope bare without modifications - Ok Choice
        const { smartChoice, okChoice, rx } = this.getChoiceEnvelopes(context.addSwapContext, context);

        const productParams = this.getBaselineProductParams(props, context);

        this.state = {
            productParams,
            smartChoices: [],
            totalSmartChoices: null,
            okChoices: [],
            totalOkChoices: null,
            products: [],
            totalProducts: 0,

            selectedProducts: [],
            selectedNutrients: {},
            remaining: {},

            loading: true,
            loadingMore: false,

            smartChoice,
            okChoice,
            rx,

            brand: null,

            selectedProduct: null,
        };

        this.debounceSearchProducts = debounce(this.searchProducts, 400);
    }

    getChildContext = () => {
        return {
            showFoodDetails: this.showFoodDetails,
        };
    };

    componentDidMount = () => {
        this.searchProducts();
        this.loadBrand();
    };

    loadBrand = async () => {
        const { selectedLocation } = this.props;

        const docs = await fetchDocumentsById([selectedLocation.parent]);

        this.setState({ brand: docs[0] });
    };

    getChoiceEnvelopes = ({ mealType, profile }) => {
        const allDayRx = profile.preferences.daily_totals;

        // mealType is always an object with name, envelope, etc.
        const mealTypeName = mealType.name;
        const rx = profile.preferences.meal_types.find((m) => m.name === mealTypeName);

        // Make two deep clones of the prescription envelope
        const smartChoice = rx && allDayRx ? transformEnvelopeForRestaurants(rx.envelope, allDayRx) : null,
            okChoice = rx && allDayRx ? transformEnvelopeForRestaurants(rx.envelope, allDayRx, true) : null;

        return { smartChoice, okChoice, rx };
    };

    getBaselineProductParams = ({ selectedLocation }, { addSwapContext }) => {
        const { profile } = addSwapContext || {};

        const params = {
            language: profile?.language || 'en',
            types: ['food'],
            terms: '',
            filters: {
                product_type: 'Restaurant Dish',
                '!tags': [],
                brand_uuid: selectedLocation.parent,
            },
            size: 50,
            sort_by: 'calories',
            sort_order: 'desc',
        };

        return params;
    };

    onChangeProductParams = (productParams) => {
        this.setState({ productParams }, this.debounceSearchProducts);
    };

    showCreateCustom = () => {
        const { showCreateCustom } = this.context;
        const { brand, productParams } = this.state;

        showCreateCustom({
            defaultFoodName: productParams.terms,
            defaultProductType: 'Restaurant Dish',
            defaultCategory: 'Restaurant Meal',
            defaultBrandName: brand?.title,
            defaultBrandUuid: brand?.uuid,
        });
    };

    showFoodDetails = (selectedProduct) => {
        this.setState({ selectedProduct });
    };

    resetProductParams = () => {
        this.onChangeProductParams(this.getBaselineProductParams(this.props, this.context));
    };

    massageViolations = (violations) => {
        return (violations || []).filter((violation) => {
            const nutrient = nutrKeys[violation.nutrNo] || allNutrients[violation.nutrNo];

            if (!nutrient) {
                return false;
            }

            const { value, min, max } = violation;

            if (min && value < min) {
                let roundedUnder = roundForHumansByUnit(min - value, nutrient.Units);

                if (!roundedUnder) {
                    return false;
                }
            }

            if (max && value > max) {
                let roundedOver = roundForHumansByUnit(value - max, nutrient.Units);

                if (!roundedOver) {
                    return false;
                }
            }

            return true;
        });
    };

    doesEnvelopeMatch = (nutrients, envelope, productName) => {
        let violations = [];

        if (!envelope) {
            return { doesMatch: false, violations };
        }

        // Use exact matching for classification, but still calculate violations for UI display
        let hasExactMismatch = false;

        Object.keys(envelope).forEach((nutrNo) => {
            // Only consider nutrients in our whitelist
            if (!nutrientWhitelist.includes(nutrNo)) {
                return;
            }

            const range = envelope[nutrNo],
                value = nutrients[nutrNo];

            if (range.min && range.min > value) {
                hasExactMismatch = true;
                violations.push({
                    nutrNo,
                    min: range.min,
                    value: value,
                    diff: range.min - value,
                });
            }

            if (range.max && range.max < value) {
                hasExactMismatch = true;
                violations.push({
                    nutrNo,
                    max: range.max,
                    value: value,
                    diff: range.max - value,
                });
            }
        });

        // For UI display, still massage the violations to hide insignificant ones
        const displayViolations = this.massageViolations(violations);

        // But for classification, use exact matching
        const doesMatch = !hasExactMismatch;

        return { doesMatch, violations: displayViolations };
    };

    analyzeResults = (results) => {
        const { addSwapContext } = this.context;
        const { smartChoice, okChoice } = this.state;
        const { profile } = addSwapContext;
        const { hide_nutrition = false } = profile;
        const avoidances = profile.preferences?.avoidances || [];

        results.forEach((product) => {
            if (!(product && product.nutrients && product.nutrients.values)) {
                return;
            }

            const avoids = avoidances.filter((tag) => (product.ingredient_tags || []).includes(tag));
            const smart = this.doesEnvelopeMatch(product.nutrients.values, smartChoice, product.name),
                ok = this.doesEnvelopeMatch(product.nutrients.values, okChoice, product.name);

            product.isSmartChoice = hide_nutrition
                ? false
                : avoids.length == 0 && smart.doesMatch && !product.tags?.includes('Not a Smart Choice');
            product.isOkChoice = hide_nutrition
                ? false
                : avoids.length == 0 && ok.doesMatch && !product.tags?.includes('Not a Smart Choice');
            product.violations = hide_nutrition ? false : smart.violations;
            product.avoids = hide_nutrition ? false : avoids;
        });

        return results;
    };

    productSearchKey = null;

    addExclusionFilters = (results, filters = {}) => {
        filters['!uuid'] = filters['!uuid'] || [];
        results.forEach((r) => {
            if (!filters['!uuid'].includes(r.uuid)) {
                filters['!uuid'].push(r.uuid);
            }
        });

        return filters;
    };

    convertEnvelopeToFiltersWithTolerance = (envelope, filters = {}, minMult = 1, factor = 1) => {
        const tolerance = 0.4999; // Tolerance to match frontend rounding behavior (< 0.5 rounds to 0)
        Object.keys(envelope).forEach(nutrNo => {
            if (!(allNutrients[nutrNo] && allNutrients[nutrNo].Filter)) {
                return;
            }

            const fieldName = allNutrients[nutrNo].Filter
            const range = envelope[nutrNo];
            const { min, max } = range;

            if (typeof min === 'number' && typeof max === 'number') {
                filters[fieldName] = {
                    'gte': Math.round((min * minMult - tolerance) * 100) / 100 / factor,
                    'lte': Math.round((max + tolerance) * 100) / 100 / factor,
                };
            } else if (typeof min !== 'number' && typeof max === 'number') {
                filters[fieldName] = {'lte': Math.round((max + tolerance) * 100) / 100 / factor};
            } else if (typeof min === 'number' && typeof max !== 'number') {
                filters[fieldName] = {'gte': Math.round((min * minMult - tolerance) * 100) / 100 / factor};
            }
        });

        return filters;
    };

    searchProducts = () => {
        const { selectedLocation } = this.props;
        const { smartChoice } = this.state;

        // Deep clone parameters
        const productSearchKey = JSON.stringify(this.state.productParams);

        if (productSearchKey === this.productSearchKey) {
            return;
        }

        this.productSearchKey = productSearchKey;
        this.searchSmartChoices();

        Analytics.viewRestaurantMenu({
            Context: 'Add/Swap',
            location_uuid: selectedLocation.uuid,
            brand_uuid: selectedLocation.parent,
        });
    };

    searchSmartChoices = () => {
        const { profile } = this.context.addSwapContext;
        const { smartChoice } = this.state;
        const params = JSON.parse(this.productSearchKey);

        // If no meal types or dish types are selected, remove the any_of_tag filter to show all items
        if (params.filters.any_of_tag && params.filters.any_of_tag.length === 0) {
            delete params.filters.any_of_tag;
        }

        // Add smart choice nutritional filters AND avoidance filters
        params.filters = smartChoice ? convertEnvelopeToFilters(smartChoice, params.filters) : params.filters;
        params.filters['!ingredient_tags'] = profile.preferences.avoidances;

        this.setState({ loading: true });

        AuthStore.fetch(getConfig('recipe_api') + '/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; schema=search/advanced/1' },
            body: JSON.stringify(params),
        }).then(
            (results) => {
                this.setState(
                    {
                        smartChoices: this.analyzeResults(results.elements),
                        totalSmartChoices: results.total,
                        loading: false,
                    },
                    () => {
                        // Only proceed to OK choices search if we didn't get a full batch of smart choices
                        if (results.elements.length < 50) {
                            this.searchOkChoices();
                        } else {
                            // We got a full batch, so set loading to false and skip other searches
                            this.setState({ loading: false });
                        }
                    }
                );
            },
            (error) => this.setState({ loading: false })
        );
    };

    searchOkChoices = () => {
        const { profile } = this.context.addSwapContext;
        const { okChoice } = this.state;
        const params = JSON.parse(this.productSearchKey);

        // Add exclusion filters for smart choices already found
        params.filters = this.addExclusionFilters(this.state.smartChoices, params.filters);

        // If no meal types or dish types are selected, remove the any_of_tag filter to show all items
        if (params.filters.any_of_tag && params.filters.any_of_tag.length === 0) {
            delete params.filters.any_of_tag;
        }

        // Add OK choice nutritional filters AND avoidance filters
        params.filters = okChoice ? convertEnvelopeToFilters(okChoice, params.filters) : params.filters;
        params.filters['!ingredient_tags'] = profile.preferences.avoidances;

        this.setState({ loading: true });

        AuthStore.fetch(getConfig('recipe_api') + '/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; schema=search/advanced/1' },
            body: JSON.stringify(params),
        }).then(
            (results) => {
                this.setState(
                    {
                        okChoices: this.analyzeResults(results.elements),
                        totalOkChoices: results.total,
                        loading: false,
                    },
                    () => {
                        // Only proceed to fallback search if we didn't get a full batch of OK choices
                        if (results.elements.length < 50) {
                            this.searchProductsFallback();
                        } else {
                            // We got a full batch, so set loading to false and skip fallback search
                            this.setState({ loading: false });
                        }
                    }
                );
            },
            (error) => this.setState({ loading: false })
        );
    };

    searchProductsFallback = () => {
        const params = JSON.parse(this.productSearchKey);

        // Exclude both smart choices and OK choices from the fallback search
        params.filters = this.addExclusionFilters(this.state.smartChoices, params.filters);
        params.filters = this.addExclusionFilters(this.state.okChoices, params.filters);

        // If no meal types or dish types are selected, remove the any_of_tag filter to show all items
        if (params.filters.any_of_tag && params.filters.any_of_tag.length === 0) {
            delete params.filters.any_of_tag;
        }

        this.setState({ loading: true });
        AuthStore.fetch(getConfig('recipe_api') + '/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; schema=search/advanced/1' },
            body: JSON.stringify(params),
        }).then(
            (results) => {
                this.setState(
                    {
                        products: this.analyzeResults(results.elements),
                        totalProducts: results.total,
                        loading: false,
                    },
                    this.syncSelectedNutrients
                );
            },
            (error) => this.setState({ loading: false })
        );
    };

    showMoreProducts = () => {
        const { profile } = this.context.addSwapContext;
        const params = JSON.parse(JSON.stringify(this.state.productParams));

        this.setState({ loadingMore: true });
        const { smartChoices, products } = this.state;
        // Add smart choices filter & exclusion filters
        params.filters = this.addExclusionFilters(smartChoices, params.filters);
        params.filters = this.addExclusionFilters(products, params.filters);
        params.filters = convertEnvelopeToFilters(this.state.smartChoice, params.filters);

        params.filters['!ingredient_tags'] = profile.preferences.avoidances;
        params.sort = 'calories';
        params.sort_order = 'desc';

        // If no meal types or dish types are selected, remove the any_of_tag filter to show all items
        if (params.filters.any_of_tag && params.filters.any_of_tag.length === 0) {
            delete params.filters.any_of_tag;
        }

        AuthStore.fetch(getConfig('recipe_api') + '/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; schema=search/advanced/1' },
            body: JSON.stringify(params),
        }).then(
            (results) => {
                this.setState({
                    smartChoices: smartChoices.concat(this.analyzeResults(results.elements)),
                    loadingMore: false,
                });

                if (results.elements.length < params.size) {
                    this.searchProductsFallback();
                } else {
                    this.syncSelectedNutrients();
                }
            },
            (error) => this.setState({ loadingMore: false })
        );
    };

    showMoreProductsFallback = () => {
        const params = JSON.parse(JSON.stringify(this.state.productParams));

        this.setState({ loadingMore: true });
        const { smartChoices, okChoices, products } = this.state;
        params.filters = this.addExclusionFilters(smartChoices, params.filters);
        params.filters = this.addExclusionFilters(okChoices, params.filters);
        params.filters = this.addExclusionFilters(products, params.filters);
        params.sort = 'calories';
        params.sort_order = 'desc';

        // If no meal types or dish types are selected, remove the any_of_tag filter to show all items
        if (params.filters.any_of_tag && params.filters.any_of_tag.length === 0) {
            delete params.filters.any_of_tag;
        }

        AuthStore.fetch(getConfig('recipe_api') + '/search', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json; schema=search/advanced/1' },
            body: JSON.stringify(params),
        }).then(
            (results) => {
                this.setState({
                    products: products.concat(this.analyzeResults(results.elements)),
                    loadingMore: false,
                });
            },
            (error) => this.setState({ loadingMore: false })
        );
    };

    showFiltersModal = () => {
        Analytics.advancedSearch({ Context: 'Restaurant Search' });
        this.setState({ isFiltersModalOpen: true });
    };

    closeModal = () => {
        this.setState({ isFiltersModalOpen: false, selectedProduct: null });
    };

    syncSelectedNutrients = () => {
        const { selectedPortions, selectedProducts, smartChoice, smartChoices, okChoices, products } = this.state;
        const { profile } = this.context.addSwapContext;

        const selectableUuids = smartChoices.map((r) => r.uuid)
            .concat(okChoices.map((r) => r.uuid))
            .concat(products.map((r) => r.uuid));

        // Ensure that selectedProducts uuids are always included in either smartChoices or products array
        const eligibleSelectedProducts = selectedProducts.filter((uuid) => selectableUuids.includes(uuid));

        const assets = indexBy(smartChoices.concat(okChoices).concat(products), 'uuid');
        const selectedNutrients = {};

        // First, aggregate the nutrients from the selected products
        eligibleSelectedProducts.forEach((uuid) => {
            const food = assets[uuid];

            if (!food?.nutrients?.values) {
                return;
            }

            const {
                logged_amount = 1,
                logged_grams,
            } = (selectedPortions && selectedPortions[food.uuid]) || {};

            // Aggregate nutrients from the selected products. We ignore the users portion size here
            // because products are not sold in variable portion sizes. You eat what you get from the store.
            Object.keys(food.nutrients.values).forEach((nutrNo) => {
                const value = food.nutrients.values[nutrNo] || 0;
                selectedNutrients[nutrNo] = selectedNutrients[nutrNo] || 0;

                if (logged_grams) {
                    selectedNutrients[nutrNo] += (value / food.grams_per_serving) * logged_grams;
                } else {
                    selectedNutrients[nutrNo] += value * logged_amount;
                }
            });
        });

        // Second, find or generate the rx for this profile / mealType
        const allDayRx = profile.preferences.daily_totals;

        if (!allDayRx) {
            this.setState({ eligibleSelectedProducts, selectedNutrients, remaining: {} });
            return;
        }

        // Third, count the remaining amounts left / over for macros and calories.
        const remaining = {};
        nutrientWhitelist.forEach((nutrNo) => {
            // If the user is not tracking this macro, we can't calculate how much should remain. Skip it.
            if (!smartChoice || !smartChoice[nutrNo]) {
                return;
            }

            // If for some weird reason the min is filled in, but the max isn't, we can subsitute the min value for
            // the max value. Don't tell anyone tho ;)
            const max = smartChoice[nutrNo].max || smartChoice[nutrNo].min;
            const value = selectedNutrients[nutrNo] || 0;

            remaining[nutrNo] = {
                percent: value < max ? Math.round((value / max) * 100) : 100,
                diff: max - value,
            };
        });

        this.setState({ eligibleSelectedProducts, selectedNutrients, remaining });
    };

    toggleProduct = (uuid) => {
        let { selectedProducts } = this.state;

        if (selectedProducts.includes(uuid)) {
            selectedProducts.splice(selectedProducts.indexOf(uuid), 1);
        } else {
            selectedProducts.push(uuid);
        }

        this.setState({ selectedProducts }, this.syncSelectedNutrients);

        return false;
    };

    addSelectedProducts = () => {
        const { selectedLocation } = this.props;
        const { smartChoices, okChoices, products, selectedProducts } = this.state;
        const { onSelectProducts } = this.context;

        if (!(selectedProducts && selectedProducts.length)) {
            return;
        }

        const allProducts = smartChoices.concat(okChoices).concat(products);

        onSelectProducts(
            selectedProducts.map((uuid) => allProducts.find((p) => p.uuid === uuid)),
            selectedLocation
        );
    };

    renderProductResult = (product, i) => {
        const { selectedProducts } = this.state;
        const { isMobile } = this.context;
        const { profile } = this.context.addSwapContext;
        const { hide_nutrition = false, rd_override = false } = profile;

        let choiceType = 'normal';

        const containsAvoidances = product.ingredient_tags.filter(ingredient => profile.preferences.avoidances.includes(ingredient));

        if (product.isSmartChoice) {
            choiceType = 'smart';
        } else if (product.isOkChoice) {
            choiceType = 'ok';
        }

        const kcalPerServing = (product.nutrients && product.nutrients.values && product.nutrients.values[208]) || 0;
        let kcal = kcalPerServing;
        const violationsPopupPositionClass = isMobile ? 'el-popup-top-right-center' : 'el-popup-right-bottom';

        return (
            <li
                key={i}
                className="product-result-item"
                data-choice-type={choiceType}
                data-selected={selectedProducts && selectedProducts.includes(product.uuid)}
            >
                <button
                    className="select-product-btn"
                    data-selected={selectedProducts && selectedProducts.includes(product.uuid)}
                    onClick={(ev) => this.toggleProduct(product.uuid)}
                >
                    &nbsp;
                </button>

                <div className="product-stats">
                    {product?.violations?.length || product?.avoids?.length ? (
                        <ViolationsPopup
                            contextName="Restaurant Meal"
                            violations={product.violations}
                            avoidances={product.avoids}
                            positionClass={violationsPopupPositionClass}
                        />
                    ) : null}

                    {choiceType !== 'normal' ? <span className="choice-type">{choiceType} choice</span> : null}

                    {!(hide_nutrition || rd_override) ? <span className="kcal">{roundForHumans(kcal)}</span> : null}
                </div>

                <div className="product-result-container">
                    <h4 onClick={() => this.showFoodDetails(product)}>{product.name}</h4>
                    {product.description ? (
                        <p onClick={() => this.showFoodDetails(product)}>{product.description}</p>
                    ) : (
                        product.description
                    )}
                    <p onClick={() => this.showFoodDetails(product)}>
                        {product.serving_description ? <span>Serving Size: {product.serving_description}</span> : null}
                        {product.grams_per_serving && !(hide_nutrition || rd_override) ? (
                            <span>{roundForHumans(product.grams_per_serving)} grams</span>
                        ) : null}
                    </p>
                    {containsAvoidances.length ?
                        <span className="contains-avoidances">Contains avoidances: {containsAvoidances.join(", ")} </span>
                    : null}
                </div>
            </li>
        );
    };

    renderFiltersModal = () => {
        const { productParams, isFiltersModalOpen } = this.state;

        if (!isFiltersModalOpen) {
            return;
        }

        return (
            <FiltersModal
                params={productParams}
                onChangeParams={this.onChangeProductParams}
                closeModal={this.closeModal}
            />
        );
    };

    renderProductDetailsModal = () => {
        const { selectedLocation } = this.props;
        const { selectedProduct } = this.state;

        if (!selectedProduct) {
            return;
        }

        const onSelectProduct = (product) => {
            this.toggleProduct(product.uuid);
            this.closeModal();
        };

        return (
            <FoodDetailsModal
                closeModal={this.closeModal}
                hideFoodUnitSelector={true}
                food={selectedProduct}
                location={selectedLocation}
                onSelectFood={onSelectProduct}
            />
        );
    };

    renderNutrientDisplay = (nutrNo, name, unit, isCalories = false) => {
        const { remaining } = this.state;

        // If no data available, show "-"
        if (!remaining[nutrNo]) {
            return (
                <div className={isCalories ? "calories-left" : "macro-left"}>
                    <em>{isCalories ? '' : name}</em>
                    {!isCalories && (
                        <div className="small-progress-bar">
                            <div className="small-progress-bar-fill" style={{ width: '0%' }}></div>
                        </div>
                    )}
                    {isCalories ? <em>-</em> : '-'}
                </div>
            );
        }

        const diff = remaining[nutrNo].diff;
        const percent = remaining[nutrNo].percent;

        if (diff > 0) {
            return (
                <div className={isCalories ? "calories-left" : "macro-left"}>
                    <em>{isCalories ? '' : name}</em>
                    {!isCalories && (
                        <div className="small-progress-bar">
                            <div
                                className="small-progress-bar-fill"
                                style={{ width: percent + '%' }}
                            ></div>
                        </div>
                    )}
                    {isCalories ? (
                        <em>{Math.round(diff)}</em>
                    ) : (
                        Math.round(diff)
                    )} {unit} left
                </div>
            );
        } else if (diff < 0) {
            return (
                <div className={isCalories ? "calories-over" : "macro-over"}>
                    <em>{isCalories ? Math.abs(Math.round(diff)) : name}</em>
                    {!isCalories && (
                        <div className="small-progress-bar">
                            <div className="small-progress-bar-fill" style={{ width: '100%' }}></div>
                        </div>
                    )}
                    {!isCalories && Math.abs(Math.round(diff))} {isCalories ? ' cals over' : ` ${unit} over`}
                </div>
            );
        } else {
            // diff === 0, show as perfectly balanced
            return (
                <div className={isCalories ? "calories-left" : "macro-left"}>
                    <em>{isCalories ? '' : name}</em>
                    {!isCalories && (
                        <div className="small-progress-bar">
                            <div
                                className="small-progress-bar-fill"
                                style={{ width: '100%' }}
                            ></div>
                        </div>
                    )}
                    {isCalories ? <em>0</em> : '0'} {unit} left
                </div>
            );
        }
    };

    render = () => {
        const { selectedLocation, returnToMap } = this.props;

        const {
            loading,
            loadingMore,
            brand,
            selectedNutrients,
            selectedProducts,
            remaining,
            rx,
            products,
            productParams,
            totalProducts,
            smartChoices,
            totalSmartChoices,
            okChoices,
            totalOkChoices,
        } = this.state;

        const { profile } = this.context.addSwapContext;

        const currentLocation = this.map && this.map.currentLocation;
        const defaultNutrients = getIdealsFromEnvelope(rx?.envelope || {}, 1);
        const { hide_nutrition = false, rd_override = false } = profile;

        const demographic = getDemographic(profile);
        const dri = {
            ...allRecommendations['all-ages'],
            ...allRecommendations[demographic],
        };

        ['203', '204', '205'].forEach((nutrNo) => {
            if (!defaultNutrients[nutrNo]) {
                defaultNutrients[nutrNo] = dri[nutrNo];
            }
        });

        return (
            <div className="restaurant-menu">
                <header>
                    <button className="return-to-locations-btn el-btn el-medium-btn el-raspberry-outline-btn el-medium-wide-btn" onClick={returnToMap}>
                        <i className="icon-chevron-left" />
                        Back to Restaurant List
                    </button>
                </header>

                <LocationInfo brand={brand} location={selectedLocation} />

                {!(hide_nutrition || rd_override) ? (
                    <section className="nutrition-analysis">
                        <h3>Recommended Meal Limits:</h3>

                        {this.renderNutrientDisplay('208', '', 'cals', true)}

                        <MacrosPieChart nutrients={selectedNutrients} macrosGoals={defaultNutrients} />

                        {this.renderNutrientDisplay('205', 'CARBS', 'g')}
                        {this.renderNutrientDisplay('203', 'PROTEIN', 'g')}
                        {this.renderNutrientDisplay('204', 'FAT', 'g')}
                        {this.renderNutrientDisplay('307', 'SODIUM', 'mg')}
                        {this.renderNutrientDisplay('ASG', 'ADDED SUGAR', 'g')}
                    </section>
                ) : null}

                <section className="product-results-list" data-showing-nutrients={!(hide_nutrition || rd_override)}>
                    <header>
                        <SearchKeywords
                            params={productParams}
                            className="product-keyword-search"
                            onChangeParams={this.onChangeProductParams}
                            placeholder="Search menu items"
                        />

                        <button className="open-filters-btn" onClick={this.showFiltersModal}>
                            <i className="icon-settings4" />
                        </button>
                    </header>

                    {loading && !smartChoices.length && !okChoices.length && !products.length ? (
                        <div className="loading">
                            <i className="icon-spinner2" />
                        </div>
                    ) : null}

                    {smartChoices.length || okChoices.length || products.length ? (
                        <ul>
                            {smartChoices.map(this.renderProductResult)}
                            {okChoices.map(this.renderProductResult)}
                            {products.map(this.renderProductResult)}
                        </ul>
                    ) : null}

                    {!smartChoices.length && !okChoices.length && !products.length && !loading ? (
                        <div className="no-results-found el-fonts">
                            <p className="p4">No results found</p>

                            <footer>
                                <button
                                    className="el-medium-btn el-raspberry-btn create-meal-btn"
                                    onClick={this.showCreateCustom}
                                >
                                    Create a Meal
                                </button>
                            </footer>
                        </div>
                    ) : null}

                    <footer>
                        <Paginator
                            loading={loading}
                            loadingMore={loadingMore}
                            total={totalSmartChoices}
                            results={smartChoices}
                            showMore={this.showMoreProducts}
                        />

                        <Paginator
                            loading={loading}
                            loadingMore={loadingMore}
                            total={totalProducts}
                            results={products}
                            showMore={this.showMoreProductsFallback}
                        />
                    </footer>
                </section>

                <footer>
                    <ContactFormModal defaultSubject="report-issue" sourceLabel="Restaurant Menu - Report Issue" className="report-issue-btn">Report Issue</ContactFormModal>
                    <button className="cancel-btn" onClick={returnToMap}>
                        Cancel
                    </button>
                    <button
                        disabled={selectedProducts.length ? false : true}
                        className="ok-btn"
                        onClick={this.addSelectedProducts}
                    >
                        Add{selectedProducts.length ? ' (' + selectedProducts.length + ')' : null}
                    </button>
                </footer>
                {this.renderFiltersModal()}
                {this.renderProductDetailsModal()}
            </div>
        );
    };
}
